
import { Gender } from "./Gender";
import { Validation } from "./Validation";
import DemoServer from "./DemoServer";
import { ClientStatus } from "../shared/ClientStatus"
import { AccountStatus } from "../shared/AccountStatus"
import { CreatedBy } from "../shared/CreatedBy"
import CategoryTypeJson from "../shared/CategoryTypeJson"
import ContactJson, { ContactMailingJson, defaultContactJson, defaultContactMailingJson, validateClientContactJson, validateClientContactMailJson, validateClientContactJsonAddNew, validateClientContactMailJsonAddNew, validateClientContactJsonRequest, validateMailingRecipientContactJsonAddNew, validateMailingRecipientContactJson } from "../shared/ContactJson"
import MailingRecipientJson, { defaultMailingRecipientJson, MailingRecipientCustomerInfoJson, validateMailingRecipientJson, validateMailingRecipientJsonRequestForm, validateMailingRecipientJsonAdd } from "../shared/MailingRecipientJson"
import EventBookingJson from "../shared/EventBookingJson";
import RentalBookingJson from "../shared/RentalBookingJson";
import SepaMandateJson, { validateSepaMandate, validateSepaMandateAddNew } from "../shared/SepaMandateJson"

/**
 * Kontakt Daten für zukünftig alle Bereiche in denen Kontakt Daten benötigt werden.
 */
export default interface ClientJson {
   ID: number | null;

   /**
    * Status des Kontakts. Zum Aussortieren von alten Kontakten.
    */
   state: ClientStatus;

   /**
    * Zugehörigkeit/Kategorie des Kontakts.
    */
   types: CategoryTypeJson[] | null;

   /**
    * Kategorien für Verleih-Kunden für spezielle Konditionen.
    */
   rentalTypes: string[];

   /**
    * Notizen, Anmerkungen, sonstige Hinweise.
    */
   remarks: string;

   /**
    * Hauptkontakt-Daten des Kontakts.
    */
   contact: ContactJson | null;

   /**
    * Kontakt-Daten von Angehörigen, Kindern, ...
    */
   relatives: ContactJson[] | null;

   /**
    * Zugehörige Mailing-Infos falls angemeldet.
    */
   mailingRecipient: MailingRecipientJson | null;

   /**
    * Mit diesem Client/Account getätigte eventBookings.
    */
   eventBookings: EventBookingJson[] | null;

   /**
    * Mit diesem Client/Account getätigte rentalBookings.
    */
   rentalBookings: RentalBookingJson[] | null;

   /**
    * Eindeutiger Benutzername. Kann auch null sein, wenn kein Login eingerichtet ist.
    */
   username: string | null;

   /**
    * Status des Accounts/Zugangs. Zur Bestätigung neuer Accounts/Zugänge.
    */
   accountState: AccountStatus;

   /**
    * Datum und Uhrzeit des letzten Logins.
    */
   lastAccountLoginDateTime: string | null;

   /**
    * SEPA-Lastschriftmandat für die Zahlung. Kann auch null sein, wenn eine Bar oder Rechnungszahlung gewünscht ist.
    */
   sepaMandate: SepaMandateJson | null;

   /**
    * Zeigt an von wem oder wodurch der Kontakt erstellt wurde.
    */
   createdBy: CreatedBy;
}

export type ClientMailingJson = Omit<ClientJson, "state" | "rentalTypes" | "contact" | "relatives" | "eventBookings" | "rentalBookings" | "username" | "accountState" | "lastAccountLoginDateTime" | "sepaMandate"> & { contact: ContactMailingJson | null };
export type ClientMailingSelectableJson = ClientMailingJson & { isSelected: boolean; };
export type ClientMailingCustomerInfoJson = { mailingRecipient: MailingRecipientCustomerInfoJson | null };

export function defaultClientJson(): ClientJson {
   return {
      ID: null,
      state: ClientStatus.active,
      types: [],
      rentalTypes: [],
      remarks: "",
      contact: defaultContactJson(),
      relatives: null,
      mailingRecipient: null,
      eventBookings: null,
      rentalBookings: null,
      username: null,
      accountState: AccountStatus.confirmationPending,
      lastAccountLoginDateTime: new Date().toISOString(),
      sepaMandate: null,
      createdBy: CreatedBy.customer
   };
}

export function defaultClientMailingJson(): ClientMailingJson {
   return {
      ID: null,
      types: [],
      remarks: "",
      contact: defaultContactMailingJson(),
      mailingRecipient: defaultMailingRecipientJson(),
      createdBy: CreatedBy.mailingCustomer
   };
}

export function validateClientJsonAddNew(item: ClientJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateString("remarks", 1000)
      .validateUsingFunction("contact", item.mailingRecipient || item.username ? validateClientContactMailJsonAddNew : validateClientContactJsonAddNew)
      .validateAllUsingFunction("relatives", validateClientContactJsonAddNew, 0, true)
      .validateUsingFunction("mailingRecipient", validateMailingRecipientJsonAdd, true)
      .validateUsername("username", 50, true)
      .validateDate("lastAccountLoginDateTime", true)
      .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
      .finish();
}

export function validateClientJsonRequest(item: ClientJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateString("remarks", 1000)
      .validateUsingFunction("contact", validateClientContactJsonRequest)
      .validateAllUsingFunction("relatives", validateClientContactJsonAddNew, 0, true)
      .validateUsingFunction("mailingRecipient", validateMailingRecipientJsonAdd, true)
      .validateUsername("username", 50)
      .validateDate("lastAccountLoginDateTime", true)
      .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
      .finish();
}

export function validateClientJson(item: ClientJson) {
   return new Validation(item)
      .validateInteger("ID", 0, null, true)
      .validateString("remarks", 1000)
      .validateUsingFunction("contact", item.mailingRecipient || item.username ? validateClientContactMailJson : validateClientContactJson)
      .validateAllUsingFunction("relatives", validateClientContactJson, 0, true)
      .validateUsingFunction("mailingRecipient", validateMailingRecipientJson, true)
      .validateUsername("username", 50, true)
      .validateDate("lastAccountLoginDateTime", true)
      .validateUsingFunction("sepaMandate", validateSepaMandate, true)
      .finish();
}

export function validateClientMailingRecipientJsonAddNew(item: ClientMailingJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateString("remarks", 1000)
      .validateUsingFunction("contact", validateMailingRecipientContactJsonAddNew)
      .validateUsingFunction("mailingRecipient", validateMailingRecipientJsonRequestForm)
      .finish();
}

export function validateClientMailingRecipientJsonManagementAdd(item: ClientMailingJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateString("remarks", 1000)
      .validateUsingFunction("contact", validateMailingRecipientContactJsonAddNew)
      .validateUsingFunction("mailingRecipient", validateMailingRecipientJsonAdd)
      .finish();
}

export function validateClientMailingRecipientJson(item: ClientMailingJson) {
   return new Validation(item)
      .validateInteger("ID", 0, null, true)
      .validateString("remarks", 1000)
      .validateUsingFunction("contact", validateMailingRecipientContactJson)
      .validateUsingFunction("mailingRecipient", validateMailingRecipientJson)
      .finish();
}